import React from 'react';
import i18n from 'i18next';
import { ToastContext, Accordion, SelectHierarchyLabel, Section, SelectLabel, InputLabel, Input, CurrencyInputLabel, Numbers, Button, Icon, Form, Message } from '@weezevent/nacre';
import { fetcher } from '@weezevent/weezjs-core';
import LoadingCard from '../../../../components/loadingCard';
import { Quota, RatesTree, Group } from '../../../../models';
import { useTranslation } from 'react-i18next';
import { Loader } from 'semantic-ui-react';
import AttendeeImportProcess from '../model';
import { PollingImportRequest } from './utils';
import css from './imports-steps.module.css';

export const RatesStep = ({ organization, event, form, setForm, onCardDone, onDone, openModal }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [ratesToCreate, setRatesToCreate] = React.useState([]);
    const [ratesErrors, setRatesErrors] = React.useState([]);
    const [loadingCreatingRates, setLoadingCreatingRates] = React.useState(false);
    const [importReady, setImportReady] = React.useState(true);

    const _onDone = React.useCallback(
        requestDatas => {
            onDone(requestDatas, onCardDone);
        },
        [onCardDone, onDone]
    );

    const [rates, loadingRates, syncRates] = RatesTree.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id,
            cache: false,
            allow_cache: false
        },
        [organization, event]
    );

    const [groups, loadingGroups] = Group.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id,
            cache: false,
            allow_cache: false
        },
        [organization, event]
    );

    const [quotas, loadingQuotas, syncQuotas] = Quota.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            getForRates: true,
            cache: false,
            allow_cache: false
        },
        [organization, event]
    );

    const ratesOptions = React.useMemo(() => {
        let options = [
            {
                text: (
                    <span className={css['icon_select_option']}>
                        <Icon name="plus" />
                        {t('ticket.attendees.imports.steps.rates.new_rate')}
                    </span>
                ),
                value: null
            }
        ];

        if (rates?.rates_list.length) {
            options = options.concat(
                rates
                    .toOptions({
                        filterGroup: gr => gr.removed === null && (gr.children.length || gr.rates.length)
                    })
                    .filter(entry => (entry.children ? entry.children.length : entry))
            );
        }
        return options;
    }, [rates]);

    const groupOptions = React.useMemo(() => {
        if (!groups || loadingGroups) {
            return [];
        }
        let options = [
            {
                text: t(`ticket.rates.infos.noGroups`),
                value: null
            }
        ];
        return options.concat(groups.map(gr => gr.toOption()));
    }, [groups, loadingGroups]);

    const ratesDatas = React.useMemo(() => {
        return form.requestDatas._rates_update
            .filter(r => !(typeof r.to === 'number'))
            .map(new_rate => ({
                name: new_rate.to.name,
                price: new_rate.to.price,
                group: new_rate.to.group,
                created: false
            }));
    }, [form]);

    const disabled = React.useMemo(() => {
        return loadingCreatingRates || ratesErrors.length || ratesDatas.some(r => !r.name || r.name === '');
    }, [loadingCreatingRates, ratesErrors.length, ratesDatas]);

    const handlePatchImportRequest = React.useCallback(() => {
        fetcher
            .patch(form.importRequest.url, {
                data: {
                    rates_update: form.requestDatas._rates_update,
                    step_name: 'update_rates'
                }
            })
            .then(() => {
                const onSuccessRatesMapping = resp => {
                    let requestDatas = new AttendeeImportProcess(resp);
                    _onDone(requestDatas);
                };

                const onSuccess = data => {
                    onSuccessRatesMapping(data);
                    setImportReady(true);
                };

                const onError = () => {
                    toast.error(t('ticket.common.toast_error'));
                };
                setImportReady(false);
                PollingImportRequest(fetcher, form, onSuccess, onError);
            })
            .catch(() => {
                toast.error(t('ticket.common.toast_error'));
            });
    }, [form, onCardDone, syncRates]);

    const handleRatesCreation = React.useCallback(async () => {
        let errors = [];

        if (!ratesDatas.length) {
            return handlePatchImportRequest();
        }

        setRatesToCreate(ratesDatas);
        setLoadingCreatingRates(Boolean(ratesDatas.length > 15));

        const newRates = ratesDatas.map(async (rate, index) => {
            delete rate['created'];
            rate['disabled'] = true; // rate created in import flow should be hidden by default in widget and mini-site
            await fetcher
                .post(`/ticket/organizations/${organization.id}/events/${event.id}/rates`, { data: rate })
                .then(({ response, status }) => {
                    if (status === 201) {
                        setRatesToCreate(previousRates => {
                            let previous = [...previousRates];
                            previous[index]['created'] = true;
                            return previous;
                        });
                        let datas = { ...form.requestDatas };
                        // assign rate id created to rates_udapte + rates_details
                        datas._rates_update.forEach((r, index) => {
                            if (r.to.name === rate.name) {
                                r.to = response.id;
                                datas._data.rates_details[index].rate_id = response.id;
                            }
                        });
                        setForm('requestDatas', datas);
                    }
                })
                .catch(() => {
                    errors.push(rate);
                });
        });

        Promise.all(newRates).then(() => {
            setLoadingCreatingRates(false);
            setRatesToCreate([]);
            setRatesErrors(errors);
            syncRates();
            syncQuotas();
            if (!errors.length) {
                handlePatchImportRequest();
            }
        });
    }, [form, organization, event, ratesDatas, syncRates, syncQuotas, handlePatchImportRequest]);

    return (
        <div className={css['step-body']}>
            <LoadingCard loading={loadingRates || loadingQuotas || loadingCreatingRates || !importReady}>
                {/* Message hidden until Pyvar can manage import files without rates informations. */}
                {/* <RatesInformation datas={form.requestDatas?._data.rates_details} /> */}
                {form.requestDatas?._data.rates_details?.map((rate, index) => (
                    <ImportRateAccordion
                        key={index}
                        index={index}
                        event={event}
                        rate={rate}
                        rates={rates}
                        groups={groupOptions}
                        options={ratesOptions}
                        form={form}
                        errors={ratesErrors}
                        setForm={setForm}
                        quotas={quotas}
                    />
                ))}
            </LoadingCard>
            <RatesCreationMessage rates={ratesToCreate} />
            <div className={css['step-footer']}>
                <Button className={css['step-cta']} primary label={t('ticket.common.action.continue')} onClick={handleRatesCreation} disabled={disabled} />
                <Button label={t('ticket.attendees.imports.steps.data.cta')} onClick={openModal} />
            </div>
        </div>
    );
};

const ImportRateAccordion = ({ event, index, form, setForm, rate, rates, groups, quotas, errors, options }) => {
    const { t } = useTranslation();
    const locale = i18n.language;

    const rateQuota = React.useMemo(() => {
        if (!quotas || !rate.rate_id) {
            return null;
        }
        const quota = quotas.find(q => q.rate_id === rate.rate_id);
        return quota && quota.quota_total ? quota.quota_total - quota.quota_taken : null;
    }, [rate, quotas]);

    const handleChangeRate = React.useCallback(
        value => {
            let ratesDatas = { ...form.requestDatas };

            const new_rate = rates?.rates_list?.find(r => r.id === value);
            const rate_name = new_rate?.getName() || '';
            const rate_price = new_rate?.getPrice() || '0';
            const rate_id = new_rate?.id || null;
            const group = new_rate?.group || null;
            const count = rate.count || 0;

            ratesDatas._data.rates_details[index] = { rate_name, rate_id, rate_price, count, group };
            ratesDatas._rates_update[index].to = value || { name: rate_name, price: rate_price };
            setForm('requestDatas', ratesDatas);
        },
        [form, index, rates]
    );

    const handleChangeValues = React.useCallback(
        (value, name) => {
            let ratesDatas = { ...form.requestDatas };
            ratesDatas._data.rates_details[index][`rate_${name}`] = value;
            ratesDatas._rates_update[index].to[name] = value;
            setForm('requestDatas', ratesDatas);
        },
        [form, index]
    );

    const error = React.useMemo(() => {
        return errors?.some(e => e.name === rate.rate_name);
    }, [rate, errors]);

    return (
        <Form.Field>
            <Accordion
                errors
                openByDefault
                disableToggle
                title={
                    <div className={css['rate-accordion-attendees-title']}>
                        <span>
                            {`${rate.rate_name} - `} <Numbers currency={event?.currency} locale={locale} value={rate.rate_price} showNullDecimal />
                        </span>
                        <span className={css['rate-accordion-attendees-count']}>
                            <Icon name="users" /> {t('ticket.attendees.imports.steps.rates.rate_attendees', { count: rate.count })}
                        </span>
                    </div>
                }
            >
                <SelectHierarchyLabel
                    label={t('ticket.attendees.imports.steps.rates.rates_label')}
                    placeholder={t('ticket.manager.attendees.action.change_rate.placeholderSelect')}
                    value={rate.rate_id}
                    options={options}
                    chevron
                    widthIndent="15px"
                    onChange={value => handleChangeRate(value)}
                />
                {rateQuota && rate.count > rateQuota && <Form.FieldErrors errors={[t('ticket.attendees.imports.steps.rates.rate_quota', { count: rateQuota })]} />}

                {!rate.rate_id && (
                    <Section>
                        <Form.Field>
                            <InputLabel label={t('ticket.attendees.imports.steps.rates.new_rate_label')} />
                            <Input onChange={(e, { value }) => handleChangeValues(value, 'name')} value={rate.rate_name} errors={error} />
                        </Form.Field>
                        {error && <Form.FieldErrors errors={[t('ticket.attendees.imports.steps.rates.creation.error')]} />}
                        <div className={css['rates-select-option-currency']}>
                            <CurrencyInputLabel
                                label={t('ticket.attendees.imports.steps.rates.rate_price')}
                                value={rate.rate_price}
                                currency={event?.currency}
                                min={0}
                                locale={locale}
                                numberDecimals={2}
                                onChange={({ value }) => handleChangeValues(value, 'price')}
                            />
                        </div>
                        <SelectLabel value={rate.group} label={t(`ticket.rates.infos.group`)} onChange={value => handleChangeValues(value, 'group')} options={groups} />
                    </Section>
                )}
            </Accordion>
        </Form.Field>
    );
};

const RatesCreationMessage = ({ rates }) => {
    const { t } = useTranslation();

    const ratesValues = React.useMemo(() => {
        return {
            total: rates.length || 0,
            count: rates?.filter(r => r.created).length || 0
        };
    }, [rates]);

    if (!rates?.length || Object.values(ratesValues).some(it => it === 0)) {
        return null;
    }

    return (
        <Message type="default" title={t('ticket.attendees.imports.steps.rates.creation.label')}>
            <div className={css['rates-step-loading-message']}>
                <span>{t('ticket.attendees.imports.steps.rates.creation.message')}</span>
                <span className={css['rates-step-loading-counter']}>
                    <Loader className={css['rates-step-loader']} active />
                    {t('ticket.attendees.imports.steps.rates.creation.counter', { count: ratesValues.count, total: ratesValues.total })}
                </span>
            </div>
        </Message>
    );
};

// Component currently irrelevant, hidden until pyvar can manage import files without rates informations.
// const RatesInformation = ({ datas }) => {
//     const { t } = useTranslation();

//     const ratesCount = React.useMemo(() => {
//         const count = datas.reduce(
//             (acc, rate) => {
//                 const key = rate.rate_id ? 'identified' : 'unidentified';
//                 acc[key] = (acc[key] || 0) + 1;
//                 return acc;
//             },
//             { identified: 0, unidentified: 0 }
//         );

//         if (count.unidentified !== datas.length) {
//             return {
//                 label: 'label',
//                 message: count.identified === datas.length ? 'message_all_rates_detected' : 'message',
//                 count: { count_found: count.identified, count_unfound: count.unidentified }
//             };
//         }
//         return { label: 'label_no_rates', message: 'message_no_rates', count: {} };
//     }, [datas]);

//     return (
//         <Message type="default" title={t(`ticket.attendees.imports.steps.rates.rates_information.${ratesCount.label}`)}>
//             <span>{t(`ticket.attendees.imports.steps.rates.rates_information.${ratesCount.message}`, { ...ratesCount.count })}</span>
//         </Message>
//     );
// };
